import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CheckCircle2, ArrowUpRight, Sparkles, Zap, Loader2, Tag, X
} from 'lucide-react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { useCurrency } from '../../hooks/useCurrency';
import { fetchPromoConfig, currentPromoConfig, isPromoActive } from '../../data/promoCampaign';
import { rememberPendingPurchase } from '../../utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plan display names and order
const PLAN_NAMES = {
  'free_trial': 'Free Trial',
  'basic_plan': 'Basic Plan',
  'pro_plan': 'Pro Plan',
  'pro_plus': 'Pro+ Plan',
};

const PLAN_TIERS = {
  'free_trial': 0,
  'basic_plan': 1,
  'pro_plan': 2,
  'pro_plus': 3,
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Reusable Plans Modal Component
 * Can be used anywhere in the app to show subscription plans
 */
const PlansModal = ({ 
  open, 
  onOpenChange, 
  user,
  onSuccess
}) => {
  const { formatPrice, paymentSupported } = useCurrency();
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return formatPrice(0);
    return formatPrice(amount);
  };

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const [prorationDetails, setProrationDetails] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [drillCounts, setDrillCounts] = useState(null);
  
  const [actionType, setActionType] = useState('upgrade'); // 'upgrade' | 'renew'
  const [autoRenew, setAutoRenew] = useState(true);
  const [promoConfig, setPromoConfig] = useState(currentPromoConfig);

  useEffect(() => {
    fetchPromoConfig().then(cfg => setPromoConfig(cfg));
  }, []);

  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Load plans and subscription status
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, statusRes, drillCountsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscriptions/plans`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/subscriptions/status`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/ai-drills/counts-by-tier`, { withCredentials: true }),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscriptionStatus(statusRes.data);
      setDrillCounts(drillCountsRes.data);
      
      // Set initial cycle based on current subscription
      if (statusRes.data?.billing_cycle) {
        setSelectedCycle(statusRes.data.billing_cycle);
      }
    } catch (err) {
      console.error('Failed to load plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset selection when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedPlan(null);
      setProrationDetails(null);
      setActionType('upgrade');
      setAutoRenew(true);
      // Reset coupon state
      setCouponCode('');
      setCouponError(null);
      setAppliedCoupon(null);
      setShowCouponInput(false);
    } else {
      setActionLoading(false);
    }
  }, [open]);

  // Validate coupon code
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    if (!selectedPlan || !prorationDetails) {
      setCouponError('Please select a plan first');
      return;
    }
    
    setCouponLoading(true);
    setCouponError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/discounts/validate`,
        {
          code: couponCode.toUpperCase(),
          order_type: 'subscription',
          plan_key: selectedPlan,
          order_amount: prorationDetails.subtotal
        },
        { withCredentials: true }
      );
      
      setAppliedCoupon(response.data);
      setCouponError(null);
      setShowCouponInput(false);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  // Discounted GST + total after coupon (new subscriptions only)
  const getDiscountedTotal = () => {
    if (!prorationDetails) return 0;
    const discount = appliedCoupon ? (appliedCoupon.discount_amount || 0) : 0;
    const discountedSubtotal = Math.max(0, prorationDetails.subtotal - discount);
    return discountedSubtotal + Math.round(discountedSubtotal * 0.18);
  };

  // Plan order for display
  const planOrder = ['basic_plan', 'pro_plan', 'pro_plus'];
  const sortedPlans = [...plans].sort((a, b) => planOrder.indexOf(a.plan_key) - planOrder.indexOf(b.plan_key));

  const currentPlan = user?.plan_type || user?.plan || 'free_trial';
  const hasPaidPlan = ['basic_plan', 'pro_plan', 'pro_plus'].includes(currentPlan);
  const currentCycle = subscriptionStatus?.billing_cycle || 'monthly';
  const currentPrice = subscriptionStatus?.locked_price || 0;
  const periodEnd = subscriptionStatus?.current_period_end || user?.plan_end_date || user?.subscription_end_date;
  const periodLabel = selectedCycle === '6_month' ? '6 months' : '30 days';
  const recurUnit = selectedCycle === '6_month' ? '/6mo' : '/mo';

  // Check if a plan is selectable
  const isPlanSelectable = (planKey, cycle) => {
    const currentTier = PLAN_TIERS[currentPlan] || 0;
    const targetTier = PLAN_TIERS[planKey] || 0;
    
    // Free trial can select anything
    if (currentPlan === 'free_trial') return true;
    
    // RESTRICTION: 6-month to monthly is NOT allowed
    if (currentCycle === '6_month' && cycle === 'monthly') return false;
    
    // Lower tier is NEVER selectable in upgrade dialog
    if (targetTier < currentTier) return false;
    
    // Higher tier is always selectable
    if (targetTier > currentTier) return true;
    
    // Same tier (targetTier === currentTier): only if upgrading from monthly to 6-month
    if (planKey === currentPlan) {
      if (currentCycle === 'monthly' && cycle === '6_month') return true;
      return false; // Current plan exact match or downgrade cycle
    }
    
    // Same tier but different plan key (shouldn't happen with our structure)
    return false;
  };

  // Get badge text for a plan
  const getPlanBadge = (planKey, cycle) => {
    if (planKey === currentPlan && cycle === currentCycle) {
      return { text: 'Current Plan', color: 'bg-[#808080]' };
    }
    if (planKey === currentPlan && currentCycle === 'monthly' && cycle === '6_month') {
      return { text: 'Cycle Upgrade', color: 'bg-[#8C9DFF]' };
    }
    if (planKey === 'pro_plan' && isPlanSelectable(planKey, cycle)) {
      return { text: 'Most Popular', color: 'bg-[#2E3558]' };
    }
    return null;
  };

  const localPreview = (price) => {
    const gst = Math.round(price * 0.18);
    const newPeriodEnd = new Date();
    newPeriodEnd.setMonth(newPeriodEnd.getMonth() + (selectedCycle === '6_month' ? 6 : 1));
    return {
      newFullPrice: price,
      credit: 0,
      daysRemainingOld: 0,
      subtotal: price,
      gst,
      payToday: price + gst,
      recurringWithGst: price + gst,
      newPeriodEnd,
      isManualUpgrade: false,
    };
  };

  // Handle plan selection - upgrade (paid user) fetches reset proration; new user uses local math
  const handlePlanSelect = async (planKey, plan) => {
    if (!isPlanSelectable(planKey, selectedCycle)) return;

    setSelectedPlan(planKey);
    setActionType('upgrade');

    if (hasPaidPlan) {
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/subscriptions/upgrade-preview`,
          { new_plan_key: planKey, new_billing_cycle: selectedCycle },
          { withCredentials: true }
        );
        if (response.data.success) {
          const p = response.data.proration;
          setProrationDetails({
            newFullPrice: p.new_full_price,
            credit: p.credit,
            daysRemainingOld: p.days_remaining_old,
            subtotal: p.subtotal,
            gst: p.gst,
            payToday: p.pay_today,
            recurringWithGst: p.recurring_with_gst,
            newPeriodEnd: new Date(p.new_period_end),
            isManualUpgrade: response.data.is_manual_upgrade,
          });
        }
      } catch (err) {
        console.error('Failed to fetch upgrade preview:', err);
      }
    } else {
      const newPrice = selectedCycle === 'monthly' ? plan.pricing.monthly : plan.pricing['6_month_total'];
      setProrationDetails(localPreview(newPrice));
    }
  };

  // Handle renew of the current plan (same tier + cycle, full price, extends from expiry)
  const handleRenewSelect = (plan) => {
    setSelectedPlan(currentPlan);
    setActionType('renew');
    const price = currentPrice
      || (currentCycle === 'monthly' ? plan?.pricing?.monthly : plan?.pricing?.['6_month_total'])
      || 0;
    const gst = Math.round(price * 0.18);
    const base = periodEnd ? new Date(periodEnd) : new Date();
    if (base < new Date()) base.setTime(Date.now());
    base.setMonth(base.getMonth() + (currentCycle === '6_month' ? 6 : 1));
    setProrationDetails({
      newFullPrice: price,
      credit: 0,
      daysRemainingOld: 0,
      subtotal: price,
      gst,
      payToday: price + gst,
      recurringWithGst: price + gst,
      newPeriodEnd: base,
      isManualUpgrade: false,
    });
  };

  // Handle cycle change
  const handleCycleChange = (newCycle) => {
    setSelectedCycle(newCycle);
    setSelectedPlan(null);
    setProrationDetails(null);
    setActionType('upgrade');
  };

  const confirmProration = async () => {
    let r = await axios.post(
      `${BACKEND_URL}/api/subscriptions/confirm-proration-payment`, {}, { withCredentials: true }
    );
    if (r.data && r.data.success === false) {
      await new Promise((res) => setTimeout(res, 3000));
      r = await axios.post(
        `${BACKEND_URL}/api/subscriptions/confirm-proration-payment`, {}, { withCredentials: true }
      );
    }
    return r;
  };

  // ONE checkout: subscription (auto-renew mandate + upfront charge) or one-time order.
  const openCheckout = (resp, label) => {
    // International (non-INR) users: charging not wired yet — show the localized quote.
    if (resp.dummy_intl) {
      alert(resp.message || 'International checkout is coming soon.');
      setActionLoading(false);
      return;
    }
    const onPaid = async () => {
      try {
        await confirmProration();
        if (onSuccess) onSuccess();
      } catch (err) {
        console.error('Confirmation failed:', err);
        alert('Payment received but confirmation failed. Please contact support.');
      } finally {
        window.location.reload();
      }
    };

    if (resp.subscription_id) {
      rememberPendingPurchase(resp.subscription_id);
      const rzp = new Razorpay({
        key: resp.razorpay_key,
        subscription_id: resp.subscription_id,
        name: 'gradnext',
        description: `${label} — ₹${resp.recurring_amount}/period`,
        prefill: { email: user?.email || '', name: user?.name || '', contact: user?.phone_number || user?.phone || '' },
        theme: { color: '#2E3558' },
        modal: { ondismiss: () => setActionLoading(false) },
        handler: onPaid,
      });
      rzp.open();
      return;
    }

    if (resp.proration_order_id && (resp.charge_amount || 0) > 0) {
      const rzp = new Razorpay({
        key: resp.razorpay_key,
        order_id: resp.proration_order_id,
        amount: Math.round((resp.charge_amount || 0) * 100),
        currency: 'INR',
        name: 'gradnext',
        description: label,
        prefill: { email: user?.email || '', name: user?.name || '', contact: user?.phone_number || user?.phone || '' },
        theme: { color: '#2E3558' },
        modal: { ondismiss: () => setActionLoading(false) },
        handler: onPaid,
      });
      rzp.open();
      return;
    }

    // Nothing to charge and no mandate: already activated server-side.
    if (onSuccess) onSuccess();
    window.location.reload();
  };

  // Handle subscription
  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    setActionLoading(true);
    try {
      // RENEW current plan
      if (actionType === 'renew') {
        const response = await axios.post(
          `${BACKEND_URL}/api/subscriptions/renew`,
          { auto_renew: autoRenew },
          { withCredentials: true }
        );
        onOpenChange(false);
        openCheckout(response.data, `Renew ${PLAN_NAMES[selectedPlan] || selectedPlan}`);
        return;
      }

      // UPGRADE (user already has a paid plan)
      if (hasPaidPlan) {
        const response = await axios.post(
          `${BACKEND_URL}/api/subscriptions/change-plan`,
          { new_plan_key: selectedPlan, new_billing_cycle: selectedCycle, auto_renew: autoRenew },
          { withCredentials: true }
        );

        if (response.data.type === 'immediate_upgrade') {
          onOpenChange(false);
          if (response.data.requires_proration_payment) {
            openCheckout(response.data, `Upgrade to ${PLAN_NAMES[selectedPlan] || selectedPlan}`);
          } else {
            alert(response.data.message || 'Upgrade successful!');
            if (onSuccess) onSuccess();
            window.location.reload();
          }
          return;
        }

        if (response.data.type === 'downgrade' && response.data.scheduled) {
          alert(response.data.message || 'Plan change scheduled for end of billing period.');
          if (onSuccess) onSuccess();
          onOpenChange(false);
          return;
        }
        return;
      }

      // NEW SUBSCRIPTION (free trial) - keep create flow with optional coupon
      {
        // New subscription - include coupon code if applied
        const subscriptionPayload = {
          plan_key: selectedPlan, 
          billing_cycle: selectedCycle 
        };
        
        // Add coupon code if one is applied - use the actual code entered, not the display name
        if (appliedCoupon && couponCode) {
          subscriptionPayload.coupon_code = couponCode.toUpperCase();
        }
        
        const response = await axios.post(
          `${BACKEND_URL}/api/subscriptions/create`,
          subscriptionPayload,
          { withCredentials: true }
        );
        
        // International (non-INR) users: charging not wired yet — show the localized quote.
        if (response.data.dummy_intl) {
          onOpenChange(false);
          alert(response.data.message || 'International checkout is coming soon.');
          setActionLoading(false);
          return;
        }

        // Close modal before opening Razorpay
        onOpenChange(false);

        // Check if it's a one-time order (coupon applied) or subscription (no coupon)
        if (response.data.payment_type === 'order') {
          // ONE-TIME ORDER for discounted first payment
          const options = {
            key: response.data.razorpay_key,
            amount: Math.round(response.data.amount * 100), // Convert to paise
            currency: response.data.currency || 'INR',
            name: 'gradnext',
            description: `${response.data.plan_name} - ${selectedCycle === 'monthly' ? 'Monthly' : '6-Month'} Plan`,
            order_id: response.data.order_id,
            handler: async function (paymentResponse) {
              try {
                alert('Payment successful! Activating your subscription...');
                
                const activateResponse = await axios.post(
                  `${BACKEND_URL}/api/subscriptions/activate-discounted`,
                  {
                    razorpay_order_id: paymentResponse.razorpay_order_id,
                    razorpay_payment_id: paymentResponse.razorpay_payment_id,
                    razorpay_signature: paymentResponse.razorpay_signature
                  },
                  { withCredentials: true }
                );
                console.log('Activation response:', activateResponse.data);
                
                alert('Subscription activated successfully! Refreshing...');
                window.location.reload();
              } catch (err) {
                console.error('Failed to activate:', err);
                alert('Payment successful! Please refresh the page to see your active plan.');
                window.location.reload();
              }
            },
            prefill: {
              email: user?.email || '',
              name: user?.name || '',
              contact: user?.phone_number || user?.phone || '',
            },
            theme: {
              color: '#3B82F6',
            },
            modal: {
              ondismiss: function() {
                setActionLoading(false);
              }
            }
          };
          
          rememberPendingPurchase(options.subscription_id || options.order_id);
          const rzp = new Razorpay(options);
          rzp.open();
          return;
        }
        
        // Regular subscription (no coupon)
        if (response.data.subscription_id) {
          const options = {
            key: response.data.razorpay_key,
            subscription_id: response.data.subscription_id,
            name: 'gradnext',
            description: `${response.data.plan_name} - ${selectedCycle === 'monthly' ? 'Monthly' : '6-Month'} Plan`,
            handler: async function (paymentResponse) {
              try {
                alert('Payment successful! Activating your subscription...');
                
                // Call activate endpoint to ensure subscription is activated
                // This is a fallback in case webhook doesn't process in time
                try {
                  const activateResponse = await axios.post(
                    `${BACKEND_URL}/api/subscriptions/activate`,
                    {},
                    { withCredentials: true }
                  );
                  console.log('Activation response:', activateResponse.data);
                  
                  if (activateResponse.data.success) {
                    alert('Subscription activated successfully! Refreshing...');
                  } else {
                    // If activation says not ready, wait a bit for webhook
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    // Try activation again
                    await axios.post(
                      `${BACKEND_URL}/api/subscriptions/activate`,
                      {},
                      { withCredentials: true }
                    );
                  }
                } catch (activateErr) {
                  console.log('Activation fallback:', activateErr.response?.data || activateErr);
                  // If activation fails, wait for webhook (might have already processed)
                  await new Promise(resolve => setTimeout(resolve, 3000));
                }
                
                // Reload page to get updated user data
                window.location.reload();
              } catch (err) {
                console.error('Failed to activate:', err);
                alert('Payment successful! Please refresh the page to see your active plan.');
                window.location.reload();
              }
            },
            prefill: {
              email: user?.email || '',
              name: user?.name || '',
              contact: user?.phone_number || user?.phone || '',
            },
            theme: {
              color: '#3B82F6',
            },
            modal: {
              ondismiss: function() {
                setActionLoading(false);
              }
            }
          };
          
          rememberPendingPurchase(options.subscription_id || options.order_id);
          const rzp = new Razorpay(options);
          rzp.open();
          return;
        }
      }
    } catch (err) {
      console.error('Failed to process subscription:', err);
      alert(err.response?.data?.detail || 'Failed to process subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Plan features - auto-generate from access settings + manual display_features
  const getPlanFeatures = (plan) => {
    const features = plan.features || {};
    const generatedFeatures = [];
    
    // Auto-generate from access settings
    if (features.course_recordings) {
      generatedFeatures.push(features.course_recordings_limited ? 'Limited course access' : 'Full course access');
    }
    
    // Dynamic drill counts based on plan
    if (features.drills_exercises && drillCounts) {
      const planKey = plan.plan_key;
      let tier = 'full_access'; // Default for Pro/Pro+
      
      if (planKey === 'basic_plan') {
        tier = 'basic_plan';
      } else if (planKey === 'free_trial') {
        tier = 'free_trial';
      }
      
      const counts = drillCounts.counts?.[tier] || {};
      
      if (counts.case_math > 0) {
        generatedFeatures.push(`${counts.case_math} Case Math drills`);
      }
      if (counts.case_structuring > 0) {
        generatedFeatures.push(`${counts.case_structuring} Case Structuring drills`);
      }
      if (counts.charts_exhibits > 0) {
        generatedFeatures.push(`${counts.charts_exhibits} Charts & Exhibits drills`);
      }
    } else if (features.drills_exercises) {
      // Fallback if drill counts not loaded
      generatedFeatures.push(features.drills_limited ? 'Limited drills access' : 'Drills & exercises');
    }
    
    if (features.peer_to_peer && features.peer_to_peer !== 'none') {
      const peerLabels = {
        '1_only': '1 peer practice session',
        '1_per_week': '4 peer practice sessions/month',
        '2_per_week': '8 peer practice sessions/month',
        'unlimited': 'Unlimited peer practice'
      };
      generatedFeatures.push(peerLabels[features.peer_to_peer] || features.peer_to_peer);
    } else if (features.peer_sessions_per_month !== undefined && features.peer_sessions_per_month !== null && features.peer_sessions_per_month !== 0) {
      if (features.peer_sessions_per_month === -1) {
        generatedFeatures.push('Unlimited peer practice');
      } else if (features.peer_sessions_per_month > 0) {
        generatedFeatures.push(`${features.peer_sessions_per_month} peer practice session${features.peer_sessions_per_month !== 1 ? 's' : ''}/month`);
      }
    }
    
    // Add manual display_features from admin panel
    if (plan.display_features && plan.display_features.length > 0) {
      generatedFeatures.push(...plan.display_features);
    }
    
    return generatedFeatures;
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--gn-periwinkle)' }} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--gn-rhino)' }}>
            <Sparkles className="w-5 h-5" style={{ color: 'var(--gn-periwinkle)' }} />
            {hasPaidPlan ? 'Upgrade Your Plan' : 'Choose Your Plan'}
          </DialogTitle>
          <DialogDescription>
            {hasPaidPlan
              ? 'Upgrade to a higher plan, or renew your current plan.'
              : 'Select a plan to unlock all features and accelerate your consulting career.'}
          </DialogDescription>
        </DialogHeader>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center my-3">
          <div className="inline-flex rounded-lg p-1" style={{ backgroundColor: 'var(--gn-grey-light)' }}>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedCycle === 'monthly' ? 'bg-white shadow' : ''
              }`}
              style={{ color: selectedCycle === 'monthly' ? 'var(--gn-rhino)' : 'var(--gn-grey-dark)' }}
              onClick={() => handleCycleChange('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedCycle === '6_month' ? 'bg-white shadow' : ''
              }`}
              style={{ color: selectedCycle === '6_month' ? 'var(--gn-rhino)' : 'var(--gn-grey-dark)' }}
              onClick={() => handleCycleChange('6_month')}
            >
              6 Months <span className="text-xs ml-1" style={{ color: 'var(--gn-periwinkle)' }}>{isPromoActive(promoConfig) ? (promoConfig?.badge_text || 'Save 50%') : 'Save 20%'}</span>
            </button>
          </div>
        </div>

        {/* Horizontal Layout: Plans + Summary side by side */}
        <div className="flex gap-4">
          {/* Plans Grid - Left side */}
          <div className="flex-1 grid grid-cols-3 gap-3">
            {sortedPlans.map((plan) => {
              const isSelectable = isPlanSelectable(plan.plan_key, selectedCycle);
              const isSelected = selectedPlan === plan.plan_key;
              const isCurrent = hasPaidPlan && plan.plan_key === currentPlan && selectedCycle === currentCycle;
              const badge = getPlanBadge(plan.plan_key, selectedCycle);
              const price = selectedCycle === 'monthly'
                ? plan.pricing.monthly
                : plan.pricing['6_month_per_month'];
              const totalPrice = selectedCycle === '6_month' ? plan.pricing['6_month_total'] : price;

              return (
                <div
                  key={plan.plan_key}
                  className={`relative rounded-xl border-2 p-3 transition-all ${
                    isCurrent
                      ? 'border-[#808080] bg-slate-50'
                      : !isSelectable
                      ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'border-[#2E3558] bg-[#DEE3FF] shadow-lg cursor-pointer ring-2 ring-[#B1BCFF]'
                        : 'border-slate-200 hover:border-[#8C9DFF] hover:shadow cursor-pointer'
                  }`}
                  onClick={() => isSelectable && handlePlanSelect(plan.plan_key, plan)}
                >
                  {badge && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                      <span className={`${badge.color} text-white text-xs font-medium px-2 py-0.5 rounded-full`}>
                        {badge.text}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center pt-1">
                    <h3 className="font-semibold text-sm" style={{ color: 'var(--gn-rhino)' }}>{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-2xl font-bold" style={{ color: 'var(--gn-rhino)' }}>{formatCurrency(price)}</span>
                      <span className="text-xs" style={{ color: 'var(--gn-grey)' }}>/mo</span>
                    </div>
                    {selectedCycle === '6_month' && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--gn-grey)' }}>
                        {formatCurrency(totalPrice)} billed every 6 months
                      </p>
                    )}
                  </div>

                  {/* Features List */}
                  <ul className="mt-3 space-y-1.5 text-xs">
                    {getPlanFeatures(plan).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-1.5" style={{ 
                        color: feature.includes('Everything in') ? 'var(--gn-periwinkle)' : 'var(--gn-grey-dark)',
                        fontWeight: feature.includes('Everything in') ? '500' : 'normal'
                      }}>
                        {!feature.includes('Everything in') && (
                          <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--gn-periwinkle)' }} />
                        )}
                        {feature.includes('Everything in') && (
                          <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--gn-periwinkle)' }} />
                        )}
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Renew current plan */}
                  {isCurrent && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRenewSelect(plan); }}
                      className={`mt-3 w-full text-xs font-medium rounded-md py-1.5 transition-colors ${
                        actionType === 'renew' && isSelected
                          ? 'bg-[#2E3558] text-white'
                          : 'border border-[#2E3558] text-[#2E3558] hover:bg-[#DEE3FF]'
                      }`}
                    >
                      Renew this plan
                    </button>
                  )}

                  {/* Selection indicator */}
                  {isSelected && actionType !== 'renew' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--gn-rhino)' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Proration Summary - Right side */}
          {prorationDetails && selectedPlan && (
            <div className="w-72 flex-shrink-0">
              <div className="rounded-xl p-4 h-full" style={{ backgroundColor: 'var(--gn-periwinkle-lighter)', border: '1px solid var(--gn-periwinkle-light)' }}>
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm" style={{ color: 'var(--gn-rhino)' }}>
                  <CheckCircle2 className="w-4 h-4" />
                  {actionType === 'renew' ? 'Renew Summary' : hasPaidPlan ? 'Upgrade Summary' : 'Order Summary'}
                </h4>

                {/* Immediate activation notice */}
                <div className="rounded-lg p-2 mb-3" style={{ backgroundColor: 'var(--gn-periwinkle-light)' }}>
                  <p className="font-medium flex items-center gap-1.5 text-xs" style={{ color: 'var(--gn-rhino)' }}>
                    <Zap className="w-3.5 h-3.5" />
                    {actionType === 'renew' ? 'Plan extended instantly!' : 'Active immediately!'}
                  </p>
                </div>

                {/* Manual upgrade notice */}
                {prorationDetails.isManualUpgrade && actionType !== 'renew' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                    <p className="text-xs text-amber-800">
                      <strong>Note:</strong> Your current plan was a complimentary upgrade, so no credit is applied.
                    </p>
                  </div>
                )}

                {/* Key dates */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-white rounded-lg p-2" style={{ border: '1px solid var(--gn-periwinkle-light)' }}>
                    <p className="text-xs" style={{ color: 'var(--gn-grey)' }}>Starts</p>
                    <p className="font-semibold text-sm" style={{ color: 'var(--gn-rhino)' }}>Today</p>
                  </div>
                  <div className="bg-white rounded-lg p-2" style={{ border: '1px solid var(--gn-periwinkle-light)' }}>
                    <p className="text-xs" style={{ color: 'var(--gn-grey)' }}>Renews</p>
                    <p className="font-semibold text-sm" style={{ color: 'var(--gn-rhino)' }}>{formatDate(prorationDetails.newPeriodEnd)}</p>
                  </div>
                </div>

                {/* Auto-renew toggle (upgrade / renew only) */}
                {hasPaidPlan && (
                  <button
                    type="button"
                    onClick={() => setAutoRenew((v) => !v)}
                    className="w-full flex items-center justify-between bg-white rounded-lg p-2 mb-3"
                    style={{ border: '1px solid var(--gn-periwinkle-light)' }}
                  >
                    <div className="text-left">
                      <p className="text-xs font-medium" style={{ color: 'var(--gn-rhino)' }}>Auto-renew</p>
                      <p className="text-[11px]" style={{ color: 'var(--gn-grey)' }}>
                        {autoRenew ? 'Razorpay mandate — renews automatically' : 'One-time payment, no auto-debit'}
                      </p>
                    </div>
                    <span
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${autoRenew ? 'bg-[#2E3558]' : 'bg-slate-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white transition-transform ${autoRenew ? 'translate-x-4' : 'translate-x-1'}`} />
                    </span>
                  </button>
                )}

                {/* Coupon Code Section (new subscriptions only) */}
                {!hasPaidPlan && (
                <div className="mb-3">
                  {appliedCoupon ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">{appliedCoupon.discount_name}</span>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        You save {formatCurrency(appliedCoupon.discount_amount)} on first payment!
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-2" style={{ border: '1px solid var(--gn-periwinkle-light)' }}>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Coupon code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="w-full pl-8 pr-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-slate-400"
                            style={{ borderColor: 'var(--gn-periwinkle-light)' }}
                            onKeyPress={(e) => e.key === 'Enter' && couponCode.trim() && validateCoupon()}
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={validateCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="text-xs px-3"
                          style={{ backgroundColor: 'var(--gn-rhino)' }}
                        >
                          {couponLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {couponError && (
                        <p className="text-xs text-red-500 mt-1">{couponError}</p>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Payment breakdown */}
                <div className="bg-white rounded-lg p-2 mb-3" style={{ border: '1px solid var(--gn-periwinkle-light)' }}>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--gn-grey)' }}>Payment Breakdown</p>

                  <div className="flex justify-between text-xs py-0.5">
                    <span style={{ color: 'var(--gn-grey-dark)' }}>
                      {hasPaidPlan ? `New plan (${periodLabel})` : `Plan (${periodLabel})`}
                    </span>
                    <span style={{ color: 'var(--gn-rhino)' }}>{formatCurrency(prorationDetails.newFullPrice)}</span>
                  </div>

                  {prorationDetails.credit > 0 && (
                    <div className="flex justify-between text-xs py-0.5">
                      <span style={{ color: 'var(--gn-grey-dark)' }}>Credit ({prorationDetails.daysRemainingOld}d)</span>
                      <span className="font-medium" style={{ color: 'var(--gn-periwinkle)' }}>−{formatCurrency(prorationDetails.credit)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs py-0.5 border-t mt-1 pt-1" style={{ borderColor: 'var(--gn-grey-light)' }}>
                    <span style={{ color: 'var(--gn-grey-dark)' }}>Subtotal</span>
                    <span style={{ color: 'var(--gn-rhino)' }}>{formatCurrency(prorationDetails.subtotal)}</span>
                  </div>

                  {/* Coupon discount line (new subscriptions only) */}
                  {!hasPaidPlan && appliedCoupon && appliedCoupon.discount_amount > 0 && (
                    <div className="flex justify-between text-xs py-0.5">
                      <span style={{ color: 'var(--gn-grey-dark)' }}>Coupon ({appliedCoupon.discount_name})</span>
                      <span className="font-medium text-green-600">−{formatCurrency(appliedCoupon.discount_amount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs py-0.5">
                    <span style={{ color: 'var(--gn-grey-dark)' }}>GST (18%)</span>
                    <span style={{ color: 'var(--gn-rhino)' }}>
                      {!hasPaidPlan && appliedCoupon
                        ? formatCurrency(Math.round((prorationDetails.subtotal - appliedCoupon.discount_amount) * 0.18))
                        : formatCurrency(prorationDetails.gst)
                      }
                    </span>
                  </div>

                  <div className="flex justify-between text-sm py-1 border-t mt-1 pt-1 font-semibold" style={{ borderColor: 'var(--gn-grey-light)' }}>
                    <span style={{ color: 'var(--gn-rhino)' }}>{hasPaidPlan ? 'Pay Today' : 'Total Due'}</span>
                    <span style={{ color: 'var(--gn-rhino)' }}>
                      {!hasPaidPlan && appliedCoupon
                        ? formatCurrency(getDiscountedTotal())
                        : formatCurrency(prorationDetails.payToday)
                      }
                    </span>
                  </div>

                  {/* Recurring line (auto-renew on) */}
                  {hasPaidPlan && autoRenew && (
                    <>
                      <div className="flex justify-between text-xs py-0.5 mt-1" style={{ color: 'var(--gn-grey)' }}>
                        <span>Then</span>
                        <span>{formatCurrency(prorationDetails.recurringWithGst)}{recurUnit}</span>
                      </div>
                      <p className="text-[11px] mt-1 leading-snug" style={{ color: 'var(--gn-grey)' }}>
                        Your bank's auto-pay approval may show {formatCurrency(prorationDetails.recurringWithGst)} as the limit —
                        only {formatCurrency(prorationDetails.payToday)} is charged today.
                      </p>
                    </>
                  )}

                  {/* Savings message (new subscriptions only) */}
                  {!hasPaidPlan && appliedCoupon && appliedCoupon.discount_amount > 0 && (
                    <div className="text-center mt-2 py-1 bg-green-50 rounded text-xs text-green-700 font-medium">
                      🎉 You save {formatCurrency(appliedCoupon.discount_amount)} on first payment!
                    </div>
                  )}
                </div>

                {/* Action button */}
                <Button
                  className="w-full text-white"
                  style={{ backgroundColor: 'var(--gn-rhino)' }}
                  onClick={handleSubscribe}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    actionType === 'renew'
                      ? 'Renew Now'
                      : hasPaidPlan
                        ? (autoRenew ? 'Upgrade Now' : 'Pay Once')
                        : 'Subscribe Now'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Initial message when no plan selected */}
        {!selectedPlan && (
          <div className="text-center py-6 text-sm" style={{ color: 'var(--gn-grey)' }}>
            Select a plan above to see pricing details and continue
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlansModal;
